import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight, FaCalendarAlt, FaClock } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';

const Blogs = () => {
  const [blogPosts, setBlogPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch blogs from API
  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get('https://api.marketmaestroovip.in/api/blogs');
        setBlogPosts(res.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching blogs:', err);
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate read time based on content length
  const calculateReadTime = (content) => {
    if (!content) return '1 min read';
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, ''); // Strip HTML tags
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  // Strip HTML for excerpt
  const stripHtmlTags = (html, length = 120) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'Technology': 'bg-purple-600',
      'Security': 'bg-red-600',
      'Privacy': 'bg-green-600',
      'Guides': 'bg-blue-600',
      'News': 'bg-yellow-600',
      'Tutorial': 'bg-indigo-600'
    };
    return colors[category] || 'bg-blue-600';
  };

  if (isLoading) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="min-h-screen py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-16 bg-gray-800/40 rounded-2xl">
            <p className="text-red-400 mb-4">{error}</p>
            <button 
              onClick={() => window.location.reload()}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition"
            >
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="min-h-screen py-20 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <h1 className="text-3xl md:text-4xl font-bold mb-4">
            Insights & Updates
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Stay informed with the latest news in technology, security, and digital privacy.
          </p>
        </motion.div>

        {/* Blog Grid */}
        {blogPosts.length === 0 ? (
          <div className="text-center py-16 bg-gray-800/40 rounded-2xl">
            <p className="text-gray-400 text-lg">No blog posts available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="group bg-gray-800/40 border border-gray-700 rounded-2xl overflow-hidden hover:border-blue-500/50 transition-all duration-300"
              >
                {/* Thumbnail Container */}
                <Link to={`/blog/${post.id}`} className="block relative h-48 overflow-hidden">
                  {post.image ? (
                    <img 
                      src={`https://api.marketmaestroovip.in/${post.image}`}
                      alt={post.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                         
                      onError={(e) => {
                        e.target.src = 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=800';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                      <span className="text-gray-400">No image</span>
                    </div>
                  )}
                  {post.category && (
                    <div className={`absolute top-4 left-4 ${getCategoryColor(post.category)} text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider`}>
                      {post.category}
                    </div>
                  )}
                </Link>

                {/* Content Container */}
                <div className="p-6">
                  <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                    <span className="flex items-center gap-1">
                      <FaCalendarAlt className="text-blue-500" /> {formatDate(post.created_at)}
                    </span>
                    <span className="flex items-center gap-1">
                      <FaClock className="text-blue-500" /> {calculateReadTime(post.content)}
                    </span>
                  </div>
                  
                  <Link to={`/blog/${post.id}`}>
                    <h4 className="text-xl font-bold mb-3 group-hover:text-blue-400 transition-colors line-clamp-2">
                      {post.title}
                    </h4>
                  </Link>
                  
                  <p className="text-gray-400 text-sm mb-6 line-clamp-3">
                    {stripHtmlTags(post.content, 120)}
                  </p>

                  <Link 
                    to={`/blog/${post.id}`}
                    className="inline-flex items-center gap-2 text-blue-400 font-semibold hover:text-blue-300 transition-colors group/btn"
                  >
                    Read More 
                    <FaArrowRight className="text-sm group-hover/btn:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Blogs;